// Card "Añadir modelo / fondo" para los grids de 2 columnas de los
// pickers en desktop. Tiene la MISMA medida que las otras cards (la
// className la fija el caller) y, al tocarla, abre un popover anclado con
// las opciones (crear / subir / describir / varios). El popover se
// posiciona con `position: fixed` vía portal a `document.body` para que NO
// lo recorte el contenedor scrolleable del grid, y se abre hacia arriba o
// abajo según el espacio disponible.

import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronRight, Plus } from 'lucide-react';

export interface AddCardOption {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

export interface AddOptionCardProps {
  /** Texto del estado base de la card (ej. "Agregar modelo"). */
  label: string;
  options: AddCardOption[];
  /** Medida de la card — debe matchear la de las otras del grid. */
  className?: string;
}

export const AddOptionCard: React.FC<AddOptionCardProps> = ({
  label,
  options,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // El menú aparece DESDE el punto donde se tocó (posición del mouse), no
  // anclado al borde de la card. Si no entra hacia abajo, se abre hacia
  // arriba desde el cursor.
  const toggle = (e: React.MouseEvent) => {
    if (open) {
      setOpen(false);
      return;
    }
    const width = 288;
    const estHeight = options.length * 58 + 16;
    // El menú aparece desde la esquina superior DERECHA del cursor: su
    // borde derecho queda en el click y se abre hacia la izquierda.
    let left = e.clientX - width;
    let top = e.clientY;
    if (left < 12) left = 12;
    if (left + width + 12 > window.innerWidth) {
      left = window.innerWidth - width - 12;
    }
    if (top + estHeight + 12 > window.innerHeight) {
      top = e.clientY - estHeight;
    }
    setPos({ top: Math.max(12, top), left, width });
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className={`${className} group rounded-2xl border-2 ${
          open ? 'border-brand-primary' : 'border-neutral-200'
        } bg-white flex flex-col items-center justify-center gap-2 hover:border-brand-primary active:scale-[0.98] transition-colors`}
      >
        <span
          className={`w-12 h-12 rounded-full inline-flex items-center justify-center transition-colors ${
            open
              ? 'bg-brand-primary text-white'
              : 'bg-neutral-100 text-[#3C3C43]/45 group-hover:bg-brand-primary group-hover:text-white'
          }`}
        >
          <Plus className="w-6 h-6" strokeWidth={2.5} />
        </span>
        <span
          className={`text-[14px] font-semibold leading-tight px-2 text-center transition-colors ${
            open
              ? 'text-brand-primary'
              : 'text-[#3C3C43]/60 group-hover:text-brand-primary'
          }`}
        >
          {label}
        </span>
      </button>
      {open &&
        pos &&
        ReactDOM.createPortal(
          <>
            <div
              className="fixed inset-0 z-[300]"
              onClick={() => setOpen(false)}
            />
            <div
              className="fixed z-[301] bg-white rounded-2xl shadow-xl border border-neutral-200 p-2 flex flex-col gap-1 animate-mode-swap"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
              {options.map((o, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    o.onClick();
                  }}
                  className="w-full inline-flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-neutral-50 text-brand-black/85 text-[14px] font-medium transition-colors text-left"
                >
                  <span className="inline-flex items-center gap-2.5 min-w-0">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-brand-primary/10 inline-flex items-center justify-center text-brand-primary">
                      {o.icon}
                    </span>
                    <span className="leading-snug">{o.label}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#3C3C43]/55 shrink-0" />
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};
